import { useEffect, useCallback, useRef, useState } from "react";
import { devLog } from "../../../utils/devLog";
import { useTranslation } from "../../../hooks/useTranslation";
import { CALLBACKS, triggerCallback } from "../../../utils/commonUtils";
import { useCheckout } from "../../../context/CheckoutContext";
import { processPayment } from "../../../api/api";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import useWebSocket from "../../../hooks/useWebSocket";

/**
 * Headless Trustly OpenBanking payment component.
 *
 * This component has no visual interface. It listens for payment triggers
 * (via CheckoutContext or window postMessage), processes the payment through
 * the Trustly gateway, and triggers an onActionRequired callback with the
 * redirect URL so the parent can navigate the user to Trustly's hosted page.
 *
 * Flow:
 *  1. Parent triggers payment via `triggerPaymentMethodEvent` or `makePayment` message
 *  2. Component validates session data and builds payment payload
 *  3. Calls processPayment API
 *  4. On success with redirect_url → triggers ACTION_REQUIRED callback
 *  5. Parent handles redirect to Trustly
 */
const AltruonTrustlyOpenBankingForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  checkoutPageConfDto,
  checkoutProcessDto,
  transparent,
  prefetchedGatewayConnection,
  tenantId,
}) => {
  const queryParams = new URLSearchParams(window.location.search);
  const sessionId = queryParams.get("session_id");

  const { t } = useTranslation();
  const { triggerPaymentMethodEvent, resetTrigger } = useCheckout();

  // Use refs to avoid stale closure issues
  const checkoutProcessDtoRef = useRef(checkoutProcessDto);
  const sessionIdRef = useRef(sessionId);
  const isProcessingRef = useRef(false);
  const [transactionId, setTransactionId] = useState(null);

  // Keep refs updated
  useEffect(() => {
    checkoutProcessDtoRef.current = checkoutProcessDto;
    sessionIdRef.current = sessionId;
  }, [checkoutProcessDto, sessionId]);

  /**
   * Get email from multiple possible sources
   */
  const getEmail = useCallback((sessionData, latestSession, eventData) => {
    const email =
      formData?.email ||
      eventData?.payload?.customerData?.email ||
      eventData?.payload?.email ||
      eventData?.sessionData?.customerData?.email ||
      eventData?.customerData?.email ||
      sessionData?.customerData?.email ||
      sessionData?.email ||
      latestSession?.customerData?.email ||
      latestSession?.email ||
      null;

    devLog("[TrustlyOpenBanking] Email resolved:", email);

    return email;
  }, [formData]);

  /**
   * Validate required fields for Trustly OpenBanking
   */
  const validatePaymentData = useCallback((email, sessionData) => {
    const missingFields = [];

    if (!email) {
      missingFields.push("email");
    }
    if (!sessionData?.paymentData?.amount) {
      missingFields.push("amount");
    }
    if (!sessionData?.paymentData?.currency) {
      missingFields.push("currency");
    }
    if (!sessionData?.paymentData?.gatewayConnectionId) {
      missingFields.push("gatewayConnectionId");
    }

    return missingFields;
  }, []);

  /**
   * Handle Trustly OpenBanking payment processing
   * @param {Object} eventData - Optional data from makePayment event
   */
  const handlePayment = useCallback(async (eventData = null) => {
    const currentSessionData = checkoutProcessDtoRef.current;
    const currentSessionId = sessionIdRef.current;

    devLog("[TrustlyOpenBanking] Starting payment process...");
    devLog("[TrustlyOpenBanking] Session data from props:", currentSessionData);
    devLog("[TrustlyOpenBanking] Event data received:", eventData);

    try {
      // Small delay to allow session update to propagate
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Fetch latest session to get the most up-to-date data
      const latestSession = await fetchSession(currentSessionId);
      devLog("[TrustlyOpenBanking] Latest session from API:", latestSession);

      // Get email from multiple possible sources (including event data)
      const email = getEmail(currentSessionData, latestSession, eventData);

      // Use the most complete session data available
      const sessionData = latestSession || currentSessionData;

      // Validate required fields
      const missingFields = validatePaymentData(email, sessionData);

      if (missingFields.length > 0) {
        devLog("[TrustlyOpenBanking] Missing required fields:", missingFields);

        triggerCallback(CALLBACKS.DATA_REQUIRED, {
          missingFields,
          message: missingFields.includes("email")
            ? t("checkout.paymentMethods.openbanking.emailRequired") ||
            "Customer email is required for OpenBanking payments"
            : t("checkout.paymentMethods.openbanking.missingData") ||
            "Required payment data is missing",
        });
        return;
      }

      // Build customer data with email from resolved source
      const customerData = {
        ...sessionData.customerData,
        email: email,
        firstName: formData?.firstName || sessionData?.customerData?.firstName,
        lastName: formData?.lastName || sessionData?.customerData?.lastName,
        phone: formData?.phone || sessionData?.customerData?.phone,
      };

      // Prepare payment request payload
      const paymentPayload = {
        paymentData: {
          gatewayConnectionId: sessionData.paymentData.gatewayConnectionId,
          gatewayName: sessionData.paymentData.gatewayName || "trustly",
          paymentMethod: "openbanking",
          transactionId: "",
          additionalData: {
            payment_method_flow: "REDIRECT",
            ...(sessionData.paymentData.additionalData || {}),
          },
        },
        customerData: customerData,
        billingData: sessionData.billingData,
      };

      devLog("[TrustlyOpenBanking] Sending payment request:", paymentPayload);
      devLog("[TrustlyOpenBanking] Using billingPlatformId:", billingPlatformId);
      devLog("[TrustlyOpenBanking] Using sessionId:", currentSessionId);

      // Update session with payment data before processing
      await updateSession(currentSessionId, paymentPayload);

      // Small delay after session update
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Call backend API
      const response = await processPayment(
        billingPlatformId,
        currentSessionId,
        paymentPayload
      );

      devLog("[TrustlyOpenBanking] Backend response:", response);
      devLog("[TrustlyOpenBanking] Backend response data:", response.data);

      // Handle successful response
      if (response.status === 200) {
        const data = response.data;

        // Check if redirect is required
        const redirectUrl = data.redirect_url || data.redirectUrl;

        if (redirectUrl) {
          devLog("[TrustlyOpenBanking] Redirect required:", redirectUrl);
          setTransactionId(data.newTransactionId || data.transactionId);
          triggerCallback(CALLBACKS.ACTION_REQUIRED, {
            actionType: "REDIRECT",
            redirectUrl: redirectUrl,
            paymentMethod: "openbanking",
            gateway: "trustly",
            transactionId: data.newTransactionId,
          });

          // Also notify parent window directly
          window.parent.postMessage(
            {
              type: "onActionRequired",
              payload: {
                actionType: "REDIRECT",
                redirectUrl: redirectUrl,
                paymentMethod: "openbanking",
                gateway: "trustly",
                transactionId: data.newTransactionId,
              },
            },
            "*"
          );

          return;
        }

        // Direct success (no redirect needed — unlikely for OpenBanking but handle gracefully)
        devLog("[TrustlyOpenBanking] Payment successful:", data.transactionId);

        triggerCallback(CALLBACKS.SUCCESS, {
          status: "success",
          transactionId: data.transactionId,
          paymentMethod: "openbanking",
          gateway: "trustly",
          message:
            t("checkout.paymentMethods.openbanking.success") ||
            "Payment successfully initiated.",
        });

        window.parent.postMessage(
          {
            type: "paymentSuccess",
            data: {
              transactionId: data.transactionId,
              paymentMethod: "openbanking",
              gateway: "trustly",
            },
          },
          "*"
        );

        return;
      }

      // Non-200 response
      throw new Error(response.data?.message || "Payment failed");
    } catch (error) {
      console.error("[TrustlyOpenBanking] Error:", error);

      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        t("checkout.paymentMethods.openbanking.error") ||
        "Failed to process OpenBanking payment";

      const errorCode =
        error.response?.data?.error?.code || "PAYMENT_ERROR";

      triggerCallback(CALLBACKS.FAILURE, {
        status: "failed",
        error: {
          code: errorCode,
          message: errorMessage,
          paymentMethod: "openbanking",
          gateway: "trustly",
        },
      });

      window.parent.postMessage(
        {
          type: "onFailure",
          payload: {
            error: {
              code: errorCode,
              message: errorMessage,
              paymentMethod: "openbanking",
              gateway: "trustly",
            },
          },
        },
        "*"
      );
    }
  }, [t, validatePaymentData, getEmail, formData, billingPlatformId]);

  /**
   * Handle triggerPaymentMethodEvent from checkout context
   */
  useEffect(() => {
    if (triggerPaymentMethodEvent && !isProcessingRef.current) {
      isProcessingRef.current = true;
      handlePayment(null).finally(() => {
        isProcessingRef.current = false;
        resetTrigger();
      });
    }
  }, [triggerPaymentMethodEvent, handlePayment, resetTrigger]);

  /**
   * Listen for "makePayment" from parent → trigger payment
   */
  useEffect(() => {
    const handleMessage = (event) => {
      if (event?.data?.type === "makePayment" && !isProcessingRef.current) {
        devLog("[TrustlyOpenBanking] Received makePayment event");
        devLog("[TrustlyOpenBanking] makePayment event data:", event?.data);
        isProcessingRef.current = true;
        handlePayment(event?.data).finally(() => {
          isProcessingRef.current = false;
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handlePayment]);

  /**
   * Notify parent that the payment method is ready
   */
  useEffect(() => {
    if (checkoutProcessDto?.paymentData) {
      window.parent.postMessage(
        {
          type: "formReady",
          data: {
            paymentMethod: "openbanking",
            gateway: "trustly",
          },
        },
        "*"
      );
    }
  }, [checkoutProcessDto]);


  // Listen websocket only for SDK version
  useWebSocket(transactionId, (event) => {
    const data = JSON.parse(event.data);

    if (!data?.status) return;

    // if (data.status === "success") {
    //   onParentCallback?.({
    //     type: "success",
    //     transactionId,
    //   });
    // }

    // if (data.status === "failed") {
    //   onParentCallback?.({
    //     type: "failure",
    //   });
    // }
  },
      undefined,
      undefined,
    (err) => {
      console.error("CARD WS error", err);
    }
  );

  // Headless component — no UI rendered
  return null;
};

export default AltruonTrustlyOpenBankingForm;
